import { useState, useEffect, useCallback } from "react";
import { useAccount, useWalletClient, useReadContract, useWriteContract, useWaitForTransactionReceipt } from "wagmi";
import { parseUnits, formatUnits } from "viem";
import { BrowserProvider } from "ethers";
import { MockDeFiLendingProtocolService } from "../services/contractService";
import { useWallet } from "../hooks/useWallet";
import { getEnvConfig } from "../config/env";
import type {
  LoanStatus,
  LoanApplicationDetails,
  ContractConstants,
  ApplyVerificationResult,
} from "../types/contract";

// ERC20 ABI for allowance and approval
const ERC20_ABI = [
  { type: 'function', name: 'decimals', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint8' }] },
  {
    type: 'function', name: 'allowance', stateMutability: 'view', inputs: [
      { name: 'owner', type: 'address' },
      { name: 'spender', type: 'address' }
    ], outputs: [{ type: 'uint256' }]
  },
  {
    type: 'function', name: 'approve', stateMutability: 'nonpayable', inputs: [
      { name: 'spender', type: 'address' },
      { name: 'amount', type: 'uint256' }
    ], outputs: [{ type: 'bool' }]
  },
] as const;

// Get contract addresses from environment configuration
const envConfig = getEnvConfig();
const CONTRACT_ADDRESS = envConfig.contracts.dapp!;
const COLLATERAL_CONTRACT_ADDRESS = envConfig.contracts.collateral!;
const VERIFICATION_ORACLE_CONTRACT_ADDRESS = envConfig.contracts.oracle!;

const WagmiLendingProtocolDApp = (): JSX.Element => {
  const { address, isConnected, chain } = useAccount();
  const { data: walletClient } = useWalletClient();
  const {
    connect: connectWallet,
    disconnect: disconnectWallet,
    isConnecting,
    error: walletError,
    balanceData,
  } = useWallet();
  const balanceSymbol = balanceData?.symbol === "H" ? "tHp" : balanceData?.symbol;

  // Wagmi hooks for collateral token decimals
  const { data: collateralDecimals } = useReadContract({
    address: COLLATERAL_CONTRACT_ADDRESS as `0x${string}`,
    abi: ERC20_ABI,
    functionName: 'decimals',
  });

  // Wagmi hooks for allowance checking
  const { data: allowance, refetch: refetchAllowance } = useReadContract({
    address: COLLATERAL_CONTRACT_ADDRESS as `0x${string}`,
    abi: ERC20_ABI,
    functionName: 'allowance',
    args: [address as `0x${string}`, CONTRACT_ADDRESS as `0x${string}`],
    query: { enabled: Boolean(address) }
  });

  // Wagmi hooks for approval
  const { writeContract: writeApprove, data: approveTxHash, isPending: isApprovePending } = useWriteContract();

  // Wait for approval transaction
  const { isSuccess: approveSuccess, isLoading: approveTransactionLoading } = useWaitForTransactionReceipt({
    hash: approveTxHash,
  });

  const [contractService, setContractService] =
    useState<MockDeFiLendingProtocolService | null>(null);
  const [loanApplication, setLoanApplication] =
    useState<LoanApplicationDetails | null>(null);

  const [constants, setConstants] = useState<ContractConstants | null>(null);
  const [collateralBalance, setCollateralBalance] = useState<bigint | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Section-specific states for loan application
  const [loanApplicationLoading, setLoanApplicationLoading] = useState(false);
  const [loanApplicationError, setLoanApplicationError] = useState<
    string | null
  >(null);
  const [loanApplicationSuccess, setLoanApplicationSuccess] = useState<
    string | null
  >(null);

  // Section-specific states for loan repayment
  const [repaymentLoading, setRepaymentLoading] = useState(false);
  const [repaymentError, setRepaymentError] = useState<string | null>(null);
  const [repaymentSuccess, setRepaymentSuccess] = useState<string | null>(null);

  // Section-specific states for revoke verification
  const [revokeVerificationLoading, setRevokeVerificationLoading] =
    useState(false);
  const [revokeVerificationError, setRevokeVerificationError] = useState<
    string | null
  >(null);
  const [revokeVerificationSuccess, setRevokeVerificationSuccess] = useState<
    string | null
  >(null);

  // Section-specific states for minting collateral tokens
  const [mintLoading, setMintLoading] = useState(false);
  const [mintError, setMintError] = useState<string | null>(null);
  const [mintSuccess, setMintSuccess] = useState<string | null>(null);

  // Section-specific states for updating required claims
  const [updateClaimsLoading, setUpdateClaimsLoading] = useState(false);
  const [updateClaimsError, setUpdateClaimsError] = useState<string | null>(null);
  const [updateClaimsSuccess, setUpdateClaimsSuccess] = useState<string | null>(null);

  // Polling states for loan status updates
  const [isPolling, setIsPolling] = useState(false);
  const [pollIntervalId, setPollIntervalId] = useState<NodeJS.Timeout | null>(null);
  const [autoRefreshTimeoutId, setAutoRefreshTimeoutId] = useState<NodeJS.Timeout | null>(null);

  // Auto refresh states for loan status (3 second intervals)
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);
  const [autoRefreshIntervalId, setAutoRefreshIntervalId] = useState<NodeJS.Timeout | null>(null);

  // Form states
  const [loanAmount, setLoanAmount] = useState("");
  const [collateralAmount, setCollateralAmount] = useState("");
  const [verificationFee, setVerificationFee] = useState<bigint | null>(null);
  const [debt, setDebt] = useState<bigint | null>(null);
  const [treasuryAddress, setTreasuryAddress] = useState<string | null>(null);
  // const [totalFeesCollected, setTotalFeesCollected] = useState<bigint | null>(null);
  const [repayAmount, setRepayAmount] = useState("");
  const [mintToAddress, setMintToAddress] = useState<string>(address || "");

  // User verification states
  const [verificationAddress, setVerificationAddress] = useState(address || "");
  const [attestationUID, setAttestationUID] = useState(""); // For revoking verification
  const [verificationResult, setVerificationResult] = useState<{
    verified: boolean;
    attestationUID: string;
    expiresAt: bigint;
  } | null>(null);
  const [applyVerificationResult, setApplyVerificationResult] = useState<ApplyVerificationResult | null>(null); // Raw oracle result for debugging
  const [isCheckingVerification, setIsCheckingVerification] = useState(false);

  // Required claims management states
  const [newClaimInput, setNewClaimInput] = useState("");
  const [claimsToUpdate, setClaimsToUpdate] = useState<string[]>([]);

  // Function to reset all transaction results
  const resetAllTransactionResults = useCallback(() => {
    // Clear all transaction result states
    setLoanApplicationError(null);
    setLoanApplicationSuccess(null);
    setRepaymentError(null);
    setRepaymentSuccess(null);
    setRevokeVerificationError(null);
    setRevokeVerificationSuccess(null);
    setMintError(null);
    setMintSuccess(null);
    setUpdateClaimsError(null);
    setUpdateClaimsSuccess(null);
    setError(null);
    setVerificationResult(null);

    // Clear form states
    setLoanAmount("");
    setCollateralAmount("");
    setRepayAmount("");
    setAttestationUID("");
    setNewClaimInput("");
    setClaimsToUpdate([]);

    // Clear polling if active - using current state values
    setPollIntervalId((currentId) => {
      if (currentId) {
        clearInterval(currentId);
      }
      return null;
    });
    setIsPolling(false);

    // Clear auto refresh interval if active - using current state values
    setAutoRefreshIntervalId((currentId) => {
      if (currentId) {
        clearInterval(currentId);
      }
      return null;
    });
    setIsAutoRefreshing(false);

    // DO NOT clear auto refresh timeout here - let it complete its work
    // Auto refresh timeout will clear itself after execution
  }, []);

  useEffect(() => {
    setVerificationAddress(address || "");
    setMintToAddress(address || "");
  }, [address])

  // Initialize contract service when wallet is connected
  useEffect(() => {
    // Reset all transaction results when wallet connection changes
    resetAllTransactionResults();

    // Load contract constants and user's loan application
    const loadContractData = async (
      service: MockDeFiLendingProtocolService,
    ) => {
      if (!address) return;

      setIsLoading(true);
      setError(null);

      try {
        // Try contract constants first
        const constantsData = await service.getContractConstants();
        setConstants(constantsData);

        // Then try loan application data
        const loanData = await service.getLoanApplication(address);
        setLoanApplication({
          ...loanData,
          status: Number(loanData.status) as LoanStatus, // Ensure status is a LoanStatus enum
        });

        // Load collateral balance
        const balance = await service.getCollateralBalance(address);
        setCollateralBalance(balance);

        // Load verification fee
        const verificationFeeData = await service.getVerificationFee();
        setVerificationFee(verificationFeeData);

        // Load treasury address
        const treasuryAddr = await service.getTreasuryAddress();
        setTreasuryAddress(treasuryAddr);

        // Load total fees collected
        // const totalFees = await service.getTotalFeesCollected();
        // setTotalFeesCollected(totalFees);

        // Load DApp debt amount
        // const totalDebt = await service.getDappDebt();
        // setDebt(totalDebt);

        const claimsToCheck = constantsData?.REQUIRED_CLAIMS || [];

        if (claimsToCheck.length === 0) {
          console.warn("No claims specified for verification check");
          return;
        }

        const result = await service.isUserVerified(
          verificationAddress.trim(),
          claimsToCheck,
          86400 * 60 * 30,
        );
        const verifyResult = await service.getResult(result.attestationUID!);
        setApplyVerificationResult(verifyResult);

      } catch (err: unknown) {
        const error = err as { message?: string; code?: string };
        let errorMessage = "Failed to load contract data";

        if (
          error.message?.includes("ENS") ||
          error.code === "UNSUPPORTED_OPERATION"
        ) {
          errorMessage = `Network does not support ENS operations. This is normal for custom networks. Error: ${error.message}`;
        } else if (error.message) {
          errorMessage = `Failed to load contract data: ${error.message}`;
        }

        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    if (isConnected && walletClient) {
      const initializeContract = async () => {
        try {
          // Convert wagmi wallet client to ethers provider
          const provider = new BrowserProvider(walletClient.transport);

          const service = new MockDeFiLendingProtocolService(
            CONTRACT_ADDRESS,
            COLLATERAL_CONTRACT_ADDRESS,
            VERIFICATION_ORACLE_CONTRACT_ADDRESS,
          );
          await service.initialize(provider);
          setContractService(service);

          if (address) {
            await loadContractData(service);
          }
        } catch (err) {
          const error = err as { message?: string };
          setError(`Failed to initialize contract service: ${error.message}`);
        }
      };

      initializeContract();
    } else {
      // Clean up when wallet disconnects
      setContractService((prevService) => {
        if (prevService) {
          prevService.removeAllListeners();
        }
        return null;
      });
    }
  }, [isConnected, walletClient, address]);

  // Cleanup effect when component unmounts
  useEffect(() => {
    return () => {
      // Clear any active polling interval
      if (pollIntervalId) {
        clearInterval(pollIntervalId);
      }
      // Clear any active auto refresh interval
      if (autoRefreshIntervalId) {
        clearInterval(autoRefreshIntervalId);
      }
      // Clear any active auto refresh timeout
      if (autoRefreshTimeoutId) {
        clearTimeout(autoRefreshTimeoutId);
      }
      // Remove event listeners
      if (contractService) {
        contractService.removeAllListeners();
      }
    };
  }, [pollIntervalId, autoRefreshIntervalId, autoRefreshTimeoutId, contractService]);

  // Stop polling function
  const stopPolling = () => {
    if (pollIntervalId) {
      clearInterval(pollIntervalId);
      setPollIntervalId(null);
    }
    setIsPolling(false);
  };

  // Start auto refresh function (3 second intervals)
  const startAutoRefresh = () => {
    if (!contractService || isAutoRefreshing) return;

    setIsAutoRefreshing(true);
    const intervalId = setInterval(async () => {
      await refreshLoanStatus();
    }, 3000);

    setAutoRefreshIntervalId(intervalId);
  };

  // Stop auto refresh function
  const stopAutoRefresh = () => {
    if (autoRefreshIntervalId) {
      clearInterval(autoRefreshIntervalId);
      setAutoRefreshIntervalId(null);
    }
    setIsAutoRefreshing(false);
  };

  // Cleanup polling on component unmount or address change
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  // Stop polling and auto refresh when address changes
  useEffect(() => {
    if (!address) {
      stopPolling();
      stopAutoRefresh();
    }
  }, [address]);

  // Helper function to get current allowance amount
  const getCurrentAllowance = () => {
    if (!collateralDecimals || allowance === undefined) return "0";
    return formatUnits(allowance, collateralDecimals);
  };

  // Helper function to get required collateral amount in wei
  const getRequiredCollateralWei = () => {
    if (!collateralDecimals || !collateralAmount) return BigInt(0);
    try {
      return parseUnits("100000000", collateralDecimals);
    } catch {
      return BigInt(0);
    }
  };

  // Helper function to check if approval is needed for loan application
  const isApprovalNeeded = () => {
    if (!collateralDecimals || allowance === undefined || !collateralAmount) return false;

    try {
      const requiredAmount = getRequiredCollateralWei();
      return allowance < requiredAmount;
    } catch {
      return false;
    }
  };

  // Helper function to handle collateral approval
  const handleApproveCollateral = () => {
    if (!collateralDecimals || !collateralAmount) return;

    try {
      // Calculate the exact amount needed plus a small buffer
      const requiredAmount = getRequiredCollateralWei();
      const approvalAmount = requiredAmount + (requiredAmount / BigInt(10)); // 10% buffer

      writeApprove({
        address: COLLATERAL_CONTRACT_ADDRESS as `0x${string}`,
        abi: ERC20_ABI,
        functionName: 'approve',
        args: [CONTRACT_ADDRESS as `0x${string}`, approvalAmount],
      });
    } catch (error) {
      setLoanApplicationError("Failed to initiate approval transaction");
    }
  };

  // Effect to refetch allowance after successful approval
  useEffect(() => {
    if (approveSuccess) {
      refetchAllowance();
    }
  }, [approveSuccess, refetchAllowance]);

  // Handle loan application with proper approval check
  const handleApplyForLoan = async () => {
    if (!contractService) return;

    // Final check for approval before applying
    if (isApprovalNeeded()) {
      setLoanApplicationError("Please approve collateral tokens first before applying for loan");
      return;
    }

    setLoanApplicationLoading(true);
    setLoanApplicationError(null);
    setLoanApplicationSuccess(null);

    try {
      // Double-check allowance before proceeding
      await refetchAllowance();

      // Wait a moment for the refetch to complete
      await new Promise(resolve => setTimeout(resolve, 500));

      if (isApprovalNeeded()) {
        throw new Error("Insufficient allowance. Please approve collateral tokens first.");
      }

      // Check verification fee is loaded
      if (verificationFee === null) {
        throw new Error("Verification fee not loaded. Please wait a moment and try again.");
      }


      const tx = await contractService.applyForLoan(
        loanAmount,
        collateralAmount,
        verificationFee,
      );

      // Wait for transaction confirmation
      await tx.wait();

      // Clear form first
      setLoanAmount("");
      setCollateralAmount("");

      // Show success message
      setLoanApplicationSuccess(
        `https://humanity-testnet.explorer.alchemy.com/tx/${tx.hash}`,
      );

      // Refresh verification oracle data including total fees collected
      await refreshVerificationOracleData();

      // Schedule a refresh after 3 seconds to update Status, Verified and verification Id
      // (Give more time for blockchain to process)
      // NOTE: This only updates verification status, NOT the transaction hash
      const timeoutId = setTimeout(async () => {
        console.log("Auto-refresh timeout triggered, checking conditions...");
        console.log("Address:", address, "ContractService:", !!contractService);

        if (address && contractService) {
          try {
            const loanData = await contractService.getLoanApplication(address);
            const newStatus = Number(loanData.status) as LoanStatus;
            const previousStatus = loanApplication?.status;

            console.log("Fresh loan data received:", {
              status: loanData.status,
              isVerified: loanData.isVerified,
              verificationRequestId: loanData.verificationRequestId
            });

            // Track verification status based on status transitions
            if (previousStatus === 1 && newStatus === 0) {
              // Status changed from VerificationRequested to Pending - Verification failed
            } else if (newStatus === 3 && loanData.isVerified) {
              // Status is Approved and Verified is true - Verification succeeded
            }

            setLoanApplication({
              ...loanData,
              status: newStatus,
            });
          } catch (err: unknown) {
            // Silent error handling
          }
        }
        // Clear the timeout ID after execution
        setAutoRefreshTimeoutId(null);
      }, 6000);

      // Store the timeout ID for cleanup
      setAutoRefreshTimeoutId(timeoutId);

      // Start auto-refreshing loan status every 3 seconds
      startAutoRefresh();
    } catch (err: unknown) {
      const error = err as { message?: string };
      console.error("Error during loan application:", error);
      setLoanApplicationError(`Failed to apply for loan: ${error.message}`);
    } finally {
      setLoanApplicationLoading(false);
    }
  };

  // Handle loan repayment
  const handleRepayLoan = async () => {
    if (!contractService) return;

    setRepaymentLoading(true);
    setRepaymentError(null);
    setRepaymentSuccess(null);

    try {
      const tx = await contractService.repayLoan(repayAmount);

      // Wait for transaction confirmation
      await tx.wait();

      // Refresh only loan application status after repayment
      if (address) {
        const loanData = await contractService.getLoanApplication(address);
        setLoanApplication({
          ...loanData,
          status: Number(loanData.status) as LoanStatus,
        });
      }

      // Clear form
      setRepayAmount("");

      setRepaymentSuccess(
        `https://humanity-testnet.explorer.alchemy.com/tx/${tx.hash}`,
      );
    } catch (err: unknown) {
      const error = err as { message?: string };
      setRepaymentError(`Failed to repay loan: ${error.message}`);
    } finally {
      setRepaymentLoading(false);
    }
  };

  // Handle minting collateral tokens
  const handleMintCollateralTokens = async () => {
    if (!contractService) return;

    setMintLoading(true);
    setMintError(null);
    setMintSuccess(null);

    try {
      const targetAddress = mintToAddress.trim() || address;
      if (!targetAddress) {
        setMintError("No target address specified");
        return;
      }

      const tx = await contractService.mintCollateralTokens(
        targetAddress,
        "1000", // Default mint amount of 1000 tokens
      );

      // Wait for transaction confirmation
      await tx.wait();

      // Reload collateral balance if minting to current user
      if (address && targetAddress.toLowerCase() === address.toLowerCase()) {
        const balance = await contractService.getCollateralBalance(address);
        setCollateralBalance(balance);
      }

      // Clear form - set to current address or empty string
      setMintToAddress(address || "");

      setMintSuccess(
        `https://humanity-testnet.explorer.alchemy.com/tx/${tx.hash}`,
      );
    } catch (err: unknown) {
      const error = err as { message?: string };
      setMintError(`Failed to mint collateral tokens: ${error.message}`);
    } finally {
      setMintLoading(false);
    }
  };

  // Format address for display
  const formatAddress = (addr: string) => {
    if (!addr) return "";
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  // Get loan status text
  const getLoanStatusText = (status: LoanStatus): string => {
    if (!contractService) return "Unknown";
    return contractService.getLoanStatusText(status);
  };

  // Check if loan can be repaid (verification completed and verified)
  const canRepayLoan = (loan: typeof loanApplication): boolean => {
    if (!loan) return false;
    return loan.status >= 2 && loan.isVerified;
  };

  // Refresh verification oracle data
  const refreshVerificationOracleData = async () => {
    if (!contractService) return;

    try {
      const [verificationFeeData, treasuryAddr] = await Promise.all([
        // const [verificationFeeData, treasuryAddr, totalFees] = await Promise.all([
        contractService.getVerificationFee(),
        contractService.getTreasuryAddress(),
        // contractService.getTotalFeesCollected()
      ]);
      setVerificationFee(verificationFeeData);
      setTreasuryAddress(treasuryAddr);
      // setTotalFeesCollected(totalFees);
    } catch (err: unknown) {
      const error = err as { message?: string };
    }
  };

  // Refresh loan application status and verification data
  // NOTE: This function only updates verification status, NOT the transaction hash
  const refreshLoanStatus = async () => {
    if (!address || !contractService) return;

    try {
      const loanData = await contractService.getLoanApplication(address);
      const newStatus = Number(loanData.status) as LoanStatus;
      const previousStatus = loanApplication?.status;

      // Track verification status based on status transitions
      if (previousStatus === 1 && newStatus === 0) {
        // Status changed from VerificationRequested to Pending - Verification failed
      } else if (newStatus === 3 && loanData.isVerified) {
        // Status is Approved and Verified is true - Verification succeeded
      }
      const claimsToCheck = constants?.REQUIRED_CLAIMS || [];

      if (claimsToCheck.length === 0) {
        throw new Error("No claims specified for verification check");
      }
      const result = await contractService.isUserVerified(
        verificationAddress.trim(),
        claimsToCheck,
        86400 * 60 * 30, // Default 24 hours in seconds
      );
      const verifyResult = await contractService.getResult(result.attestationUID!);

      setLoanApplication({
        ...loanData,
        status: newStatus,
      });
      setApplyVerificationResult(verifyResult);

      // Stop auto refresh if status > 1 (beyond VerificationRequested)
      if (loanData.status > 1) {
        stopAutoRefresh();
      }
    } catch (err: unknown) {
      console.log("Error refreshing loan status:", err);
      // Silent error handling
    }
  };

  // Handle revoking verification request
  const handleCheckUserVerification = async () => {
    if (!contractService) return;

    setIsCheckingVerification(true);
    setError(null);
    setVerificationResult(null);

    try {
      // Use default required claims from constants
      const claimsToCheck = constants?.REQUIRED_CLAIMS || [];

      if (claimsToCheck.length === 0) {
        throw new Error("No claims specified for verification check");
      }

      const result = await contractService.isUserVerified(
        verificationAddress.trim(),
        claimsToCheck,
        86400 * 60 * 30, // Default 24 hours in seconds
      );

      setVerificationResult(result);
    } catch (err: unknown) {
      const error = err as { message?: string };
      setError(`Failed to check user verification: ${error.message}`);
    } finally {
      setIsCheckingVerification(false);
    }
  };

  // Handle adding a claim to the list
  const handleAddClaim = () => {
    const trimmedClaim = newClaimInput.trim();
    if (trimmedClaim && !claimsToUpdate.includes(trimmedClaim)) {
      setClaimsToUpdate([...claimsToUpdate, trimmedClaim]);
      setNewClaimInput("");
    }
  };

  // Handle removing a claim from the list
  const handleRemoveClaim = (claimToRemove: string) => {
    setClaimsToUpdate(claimsToUpdate.filter(claim => claim !== claimToRemove));
  };

  // Handle initializing claims from current contract state
  const handleInitializeFromCurrentClaims = () => {
    if (constants?.REQUIRED_CLAIMS) {
      setClaimsToUpdate([...constants.REQUIRED_CLAIMS]);
    }
  };

  // Handle updating required claims
  const handleUpdateRequiredClaims = async () => {
    if (!contractService) return;

    if (claimsToUpdate.length === 0) {
      setUpdateClaimsError("Please add at least one claim");
      return;
    }

    setUpdateClaimsLoading(true);
    setUpdateClaimsError(null);
    setUpdateClaimsSuccess(null);

    try {
      const tx = await contractService.updateRequiredClaims(claimsToUpdate);

      // Wait for transaction confirmation
      await tx.wait();

      // Refresh contract constants to show updated claims
      const constantsData = await contractService.getContractConstants();
      setConstants(constantsData);

      // Clear form
      setClaimsToUpdate([]);
      setNewClaimInput("");

      setUpdateClaimsSuccess(
        `https://humanity-testnet.explorer.alchemy.com/tx/${tx.hash}`,
      );
    } catch (err: unknown) {
      const error = err as { message?: string };
      setUpdateClaimsError(`Failed to update required claims: ${error.message}`);
    } finally {
      setUpdateClaimsLoading(false);
    }
  };

  // Handle revoking verification request

  // Handle revoking verification request
  const handleRevokeVerificationRequest = async (attestationUID: string) => {
    if (!contractService) return;

    setRevokeVerificationLoading(true);
    setRevokeVerificationError(null);
    setRevokeVerificationSuccess(null);

    try {
      const tx =
        await contractService.revokeVerificationRequest(attestationUID);

      // Wait for transaction confirmation
      await tx.wait();

      // Clear verification result since it's been revoked
      setVerificationResult(null);

      // Refresh only loan application status if we're revoking our own verification
      if (
        address &&
        verificationAddress.toLowerCase() === address.toLowerCase()
      ) {
        const loanData = await contractService.getLoanApplication(address);
        setLoanApplication({
          ...loanData,
          status: Number(loanData.status) as LoanStatus,
        });
      }

      setRevokeVerificationSuccess(
        `https://humanity-testnet.explorer.alchemy.com/tx/${tx.hash}`,
      );
    } catch (err: unknown) {
      const error = err as { message?: string };
      setRevokeVerificationError(
        `Failed to revoke verification request: ${error.message}`,
      );
    } finally {
      setRevokeVerificationLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <h1 className="text-3xl font-bold text-center mb-8">
        DeFi Lending Protocol (Wagmi)
      </h1>

      {/* Wallet Connection Section */}
      <div className="bg-white shadow-md rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Wallet Connection</h2>

        {!isConnected ? (
          <div className="space-y-4">
            <p className="text-gray-600 mb-4">
              Connect your MetaMask wallet to use the DeFi Lending Protocol
            </p>

            {/* Wallet Connection Button */}
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => connectWallet()}
                disabled={isConnecting}
                className="w-full flex items-center justify-center space-x-3 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white font-medium py-3 px-6 rounded-lg transition-colors"
              >
                <span>
                  {isConnecting ? "Connecting..." : "Connect MetaMask"}
                </span>
              </button>
            </div>

            {walletError && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
                {walletError}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Wallet Connected</p>
                  <p className="text-sm">
                    Address: {formatAddress(address || "")}
                  </p>
                  <p className="text-sm">
                    Network: {chain?.name} ({chain?.id})
                  </p>
                  {collateralBalance !== null && (
                    <p className="text-sm">
                      Balance: {balanceData?.formatted} {balanceSymbol}
                    </p>
                  )}
                  {collateralBalance !== null && (
                    <p className="text-sm">
                      Collateral Balance:{" "}
                      {contractService?.formatEther(collateralBalance)}{" "}
                      (tH-test)
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={disconnectWallet}
                  className="bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Disconnect
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mint Collateral Tokens Section */}
      {isConnected && (
        <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">
            Mint Collateral Tokens
          </h2>

          <div className="space-y-4">
            {/* Mint To Address Input */}
            <div>
              <label
                htmlFor="mintToAddress"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Mint to Address (optional)
              </label>
              <input
                id="mintToAddress"
                type="text"
                value={mintToAddress || ""}
                onChange={(e) => setMintToAddress(e.target.value || "")}
                placeholder="0x... (leave empty to mint to your address)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={() => setMintToAddress(address || "")}
                disabled={!address}
                className="mt-2 text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400"
              >
                Use my address
              </button>
            </div>

            {/* Mint Button */}
            <button
              onClick={handleMintCollateralTokens}
              disabled={
                mintLoading ||
                !contractService
              }
              className="w-full bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {mintLoading ? "Minting..." : "Mint 1000 Collateral Tokens"}
            </button>

            {/* Current Collateral Balance Display */}
            {collateralBalance !== null && (
              <div className="bg-gray-50 p-3 rounded-md">
                <p className="text-sm text-gray-600">
                  Your Current Collateral Balance:{" "}
                  <span className="font-medium">
                    {contractService?.formatEther(collateralBalance)} tokens
                  </span>
                </p>
              </div>
            )}

            {/* Mint Error Display */}
            {mintError && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mt-4">
                <strong>Error:</strong> {mintError}
              </div>
            )}

            {/* Mint Success Display */}
            {mintSuccess && (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mt-4">
                <strong>Mint Success</strong>
              </div>
            )}

            {/* Show message when collateral balance is zero */}
            {collateralBalance !== null && collateralBalance === BigInt(0) && (
              <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded mt-4">
                <div className="flex items-center">
                  <span className="text-yellow-600 mr-2">ℹ️</span>
                  <div>
                    <strong>Get Started:</strong> You currently have no collateral tokens.
                    Mint some tokens above to access loan and verification features.
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {isConnected && (
        <>
          {/* Contract Constants */}
          <div className="bg-white shadow-md rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">
              Contract Information
            </h2>

            {/* Loading State */}
            {isLoading && (
              <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded mb-4">
                Loading contract data...
              </div>
            )}

            {/* Error Display */}
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                <strong>Error:</strong> {error}
              </div>
            )}

            {constants && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <p>
                  <strong>Min Loan Amount:</strong>{" "}
                  {contractService?.formatEther(constants.MIN_LOAN_AMOUNT)} tHp
                </p>
                <p>
                  <strong>Min Collateral Ratio:</strong>{" "}
                  {constants.MIN_COLLATERAL_RATIO.toString()}%
                </p>
                {/*{verificationFee !== null && (*/}
                {/*	<div className="flex items-center gap-2">*/}
                {/*		<p>*/}
                {/*			<strong>Verification Fee:</strong>{" "}*/}
                {/*			{contractService?.formatEther(verificationFee)} tHp*/}
                {/*		</p>*/}
                {/*		<button*/}
                {/*			type="button"*/}
                {/*			onClick={refreshVerificationOracleData}*/}
                {/*			className="bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium py-1 px-2 rounded transition-colors"*/}
                {/*			title="Refresh verification oracle data"*/}
                {/*		>*/}
                {/*			🔄*/}
                {/*		</button>*/}
                {/*	</div>*/}
                {/*)}*/}
                {treasuryAddress && (
                  <p>
                    <strong>Treasury Address:</strong>{" "}
                    <span className="font-mono text-xs">{treasuryAddress}</span>
                  </p>
                )}
                {/* {totalFeesCollected !== null && (
                  <p>
                    <strong>Total Fees Collected:</strong>{" "}
                    {contractService?.formatEther(totalFeesCollected)} tHp
                  </p>
                )} */}
                {debt !== null && (
                  <p>
                    <strong>DApp Debt Amount:</strong>{" "}
                    {contractService?.formatEther(debt)} tHp
                    {debt > BigInt(0) && (
                      <span className="ml-2 text-red-600 font-medium">⚠️</span>
                    )}
                  </p>
                )}
                <p>
                  <strong>Required Claims:</strong>{" "}
                  {constants?.REQUIRED_CLAIMS && constants.REQUIRED_CLAIMS.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {constants.REQUIRED_CLAIMS.map((claim, index) => (
                        <span
                          key={claim + index.toString()}
                          className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-100 text-blue-800"
                        >
                          {claim}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500">Loading...</span>
                  )}
                </p>
              </div>
            )}
          </div>

          {/* Loan Management - Combined Section */}
          <div className="bg-white shadow-md rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold mb-6">
              Loan Management
            </h2>

            {/* Current Loan Application Status */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium">Current Loan Application</h3>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={refreshLoanStatus}
                    className="bg-green-500 hover:bg-green-600 text-white text-sm font-medium py-2 px-3 rounded-lg transition-colors"
                    title="Refresh loan application status and verification data"
                  >
                    🔄 Refresh Status
                  </button>
                </div>
              </div>

              {/* Verification Status Tracking Info */}
              <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded mb-4">
                <div className="flex items-start">
                  <span className="text-blue-600 mr-2">ℹ️</span>
                  <div className="text-sm">
                    <strong>Verification Status Tracking:</strong>
                    <ul className="mt-1 list-disc list-inside space-y-1">
                      <li>When status changes from <strong>VerificationRequested</strong> to <strong>Pending</strong> → Verification failed</li>
                      <li>When status becomes <strong>Approved</strong> with Verified = true → Verification succeeded</li>
                    </ul>
                  </div>
                </div>
              </div>
              {loanApplication ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                  <div>
                    <p>
                      <strong>Amount:</strong>{" "}
                      {contractService?.formatEther(loanApplication.amount)} tHp
                    </p>
                    <p>
                      <strong>Collateral:</strong>{" "}
                      {contractService?.formatEther(loanApplication.collateral)}{" "}
                      tH
                    </p>
                  </div>
                  <div>
                    <p>
                      <strong>Status:</strong>{" "}
                      <span className={`px-2 py-1 rounded text-sm font-medium ${loanApplication.status === 1 ? 'bg-green-100 text-green-800' :
                        loanApplication.status === 2 ? 'bg-blue-100 text-blue-800' :
                          loanApplication.status === 3 ? 'bg-gray-100 text-gray-800' :
                            'bg-yellow-100 text-yellow-800'
                        }`}>
                        {getLoanStatusText(loanApplication.status)}
                      </span>
                    </p>
                    <p>
                      <strong>Verified:</strong>{" "}
                      <span className={`px-2 py-1 rounded text-sm font-medium ${loanApplication.status <= 1
                        ? 'bg-yellow-100 text-yellow-800'
                        : loanApplication.isVerified
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                        }`}>
                        {loanApplication.status <= 1
                          ? "⏳ Pending"
                          : loanApplication.isVerified ? "✓ true" : "✗ false"}
                      </span>
                    </p>
                    {loanApplication.verificationRequestId &&
                      loanApplication.verificationRequestId !==
                      "0x0000000000000000000000000000000000000000000000000000000000000000" && (
                        <div className="text-sm text-gray-600 flex items-center gap-2">
                          <strong>Verification Request ID:</strong>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                              {loanApplication.verificationRequestId.slice(0, 10)}...{loanApplication.verificationRequestId.slice(-8)}
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                if (loanApplication.verificationRequestId) {
                                  navigator.clipboard.writeText(loanApplication.verificationRequestId);
                                }
                              }}
                              className="bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs px-2 py-1 rounded transition-colors"
                              title="Copy full Request ID"
                            >
                              📋
                            </button>
                          </div>
                        </div>
                      )}
                    {loanApplication.isVerified && (
                      <>
                        <div className="flex gap-2 flex-wrap items-top" >
                          <strong>Used issuers:</strong>{" "}
                          <div className="flex flex-col items-center gap-2 mt-1">
                            {applyVerificationResult?.usedIssuers && applyVerificationResult.usedIssuers.length > 0 ? (
                              applyVerificationResult.usedIssuers.map((issuer, index) => (
                                <div key={issuer + index} className="flex items-center gap-2">
                                  <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                                    {issuer.slice(0, 10)}...{issuer.slice(-8)}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (issuer) {
                                        navigator.clipboard.writeText(issuer);
                                      }
                                    }}
                                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs px-2 py-1 rounded transition-colors"
                                    title="Copy full Request ID"
                                  >
                                    📋
                                  </button>
                                </div>
                              ))
                            ) : (
                              <span className="text-gray-500 text-center">N/A</span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 flex-wrap items-top" >
                          <strong>Used categories:</strong>{" "}
                          <div className="flex flex-col items-center gap-2 mt-1">
                            {applyVerificationResult?.usedCategories && applyVerificationResult.usedCategories.length > 0 ? (
                              applyVerificationResult.usedCategories.map((category, index) => (
                                <span key={category + index} className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                                  {category}
                                </span>
                              ))
                            ) : (
                              <span className="text-gray-500 text-center">N/A</span>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg">
                  <p className="text-lg mb-2">No active loan application</p>
                  <p className="text-sm">Apply for a loan below to get started</p>
                </div>
              )}
            </div>

            {/* Show loan actions only if user has collateral balance */}
            {collateralBalance !== null && collateralBalance > BigInt(0) ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Apply for Loan Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Apply for Loan</h3>

                  {/* Information about approval process */}
                  <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded">
                    <div className="flex items-start">
                      <span className="text-blue-600 mr-2">ℹ️</span>
                      <div>
                        User must satisfied <strong>{constants?.REQUIRED_CLAIMS.join(', ')}</strong> claims for successful verification
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label
                        htmlFor="loanAmount"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Loan Amount (tHp)
                      </label>
                      <input
                        id="loanAmount"
                        type="number"
                        step="0.001"
                        value={loanAmount}
                        onChange={(e) => {
                          setLoanAmount(e.target.value);
                          // Clear previous error messages when user starts typing
                          setLoanApplicationError(null);
                        }}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="0.01"
                      />
                      {/*<p className="text-xs text-gray-500 mt-1">*/}
                      {/*	<strong>Verification Fee:</strong>{" "}*/}
                      {/*	{contractService?.formatEther(verificationFee || BigInt(0))} ETH*/}
                      {/*</p>*/}
                    </div>
                    <div>
                      <label
                        htmlFor="collateralAmount"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Collateral Amount (tH)
                      </label>
                      <input
                        id="collateralAmount"
                        type="number"
                        step="0.001"
                        value={collateralAmount}
                        onChange={(e) => {
                          setCollateralAmount(e.target.value);
                          // Clear previous error messages when user starts typing
                          setLoanApplicationError(null);
                        }}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="0.015"
                      />
                    </div>
                  </div>

                  {/* Allowance Information */}
                  {collateralAmount && collateralDecimals && allowance !== undefined && (
                    <div className={`border px-4 py-3 rounded ${isApprovalNeeded()
                      ? "bg-yellow-50 border-yellow-200 text-yellow-800"
                      : "bg-green-50 border-green-200 text-green-800"
                      }`}>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm">
                              <strong>Current Allowance:</strong>{" "}
                              {getCurrentAllowance()} tokens
                            </p>
                            <p className="text-sm">
                              <strong>Required Amount:</strong> {collateralAmount} tokens
                            </p>
                            <p className="text-sm">
                              <strong>Status:</strong>{" "}
                              {isApprovalNeeded() ? (
                                <span className="font-medium text-yellow-700">Approval Required</span>
                              ) : (
                                <span className="font-medium text-green-700">✓ Sufficient Allowance</span>
                              )}
                            </p>
                          </div>
                          {isApprovalNeeded() && (
                            <div className="flex flex-col items-end">
                              <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded mb-1">
                                Approval Needed
                              </span>
                              <button
                                type="button"
                                onClick={() => refetchAllowance()}
                                className="text-xs text-blue-600 hover:text-blue-800 underline"
                              >
                                Refresh Allowance
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Conditional Approval/Apply Button */}
                  <div className="space-y-3">
                    {isApprovalNeeded() ? (
                      <div>
                        <button
                          type="button"
                          onClick={handleApproveCollateral}
                          disabled={
                            isApprovePending ||
                            approveTransactionLoading ||
                            !collateralAmount ||
                            !collateralDecimals
                          }
                          className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-3 px-4 rounded disabled:opacity-50 w-full"
                        >
                          {isApprovePending || approveTransactionLoading
                            ? "Approving Collateral..."
                            : `Approve ${collateralAmount} Tokens`}
                        </button>
                        <p className="text-sm text-gray-600 mt-2 text-center">
                          Step 1 of 2: Approve collateral tokens for spending
                        </p>
                      </div>
                    ) : (
                      <div>
                        <button
                          type="button"
                          onClick={handleApplyForLoan}
                          disabled={
                            loanApplicationLoading ||
                            !loanAmount ||
                            !collateralAmount ||
                            !verificationFee ||
                            isApprovalNeeded()
                          }
                          className="bg-green-500 hover:bg-green-700 text-white font-bold py-3 px-4 rounded disabled:opacity-50 w-full"
                        >
                          {loanApplicationLoading ? "Processing Loan Application..." : "Apply for Loan"}
                        </button>
                      </div>
                    )}
                  </div>

                  {/*/!* Approval Success Message *!/*/}
                  {/*{approveSuccess && !isApprovalNeeded() && (*/}
                  {/*	<div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">*/}
                  {/*		<div className="flex items-center">*/}
                  {/*			<span className="text-green-600 mr-2">✅</span>*/}
                  {/*			<div>*/}
                  {/*				<strong>Approval Successful!</strong>*/}
                  {/*				<p className="text-sm mt-1">Collateral tokens approved. You can now proceed with your loan application.</p>*/}
                  {/*			</div>*/}
                  {/*		</div>*/}
                  {/*	</div>*/}
                  {/*)}*/}

                  {/* Approval Transaction Pending */}
                  {(isApprovePending || approveTransactionLoading) && (
                    <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded">
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700 mr-2"></div>
                        <div>
                          <strong>Processing Approval...</strong>
                          <p className="text-sm mt-1">Please wait while your approval transaction is being processed.</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Loan Application Error Display */}
                  {loanApplicationError && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                      <strong>Error:</strong> {loanApplicationError}
                    </div>
                  )}

                  {/* Loan Application Success Display */}
                  {loanApplicationSuccess && (
                    <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                      <strong>Loan application successful!</strong>{" "}
                      <a
                        href={loanApplicationSuccess}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-800 underline hover:text-green-900"
                      >
                        View Transaction
                      </a>
                    </div>
                  )}
                </div>

                {/* Repay Loan Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Repay Loan</h3>

                  {/* Verification Status Check */}
                  {!canRepayLoan(loanApplication) && (
                    <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded">
                      <div className="flex items-center">
                        <span className="text-yellow-600 mr-2">⚠️</span>
                        <div>
                          <strong>Verification Required:</strong>
                          <p className="text-sm mt-1">
                            You must be verified to repay your loan. Please complete the verification process first.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-3">
                    <div>
                      <label
                        htmlFor="repayAmount"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Repayment Amount (ETH)
                      </label>
                      <input
                        id="repayAmount"
                        type="number"
                        step="0.001"
                        value={repayAmount}
                        onChange={(e) => {
                          setRepayAmount(e.target.value);
                          // Clear previous messages when user starts typing
                          setRepaymentError(null);
                          setRepaymentSuccess(null);
                        }}
                        disabled={!canRepayLoan(loanApplication)}
                        className={`block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${!canRepayLoan(loanApplication) ? 'bg-gray-100 cursor-not-allowed' : ''
                          }`}
                        placeholder="0.01"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleRepayLoan}
                      disabled={
                        repaymentLoading ||
                        !repayAmount ||
                        !canRepayLoan(loanApplication)
                      }
                      className={`font-bold py-3 px-4 rounded transition-colors w-full ${!canRepayLoan(loanApplication)
                        ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                        : 'bg-blue-500 hover:bg-blue-700 text-white disabled:opacity-50'
                        }`}
                      title={!canRepayLoan(loanApplication) ? 'Verification required to repay loan' : ''}
                    >
                      {repaymentLoading ? "Processing..." : "Repay Loan"}
                    </button>
                  </div>

                  {/* Repayment Error Display */}
                  {repaymentError && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                      <strong>Error:</strong> {repaymentError}
                    </div>
                  )}

                  {/* Repayment Success Display */}
                  {repaymentSuccess && (
                    <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                      <strong>Repayment successful!</strong>{" "}
                      <a
                        href={repaymentSuccess}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-800 underline hover:text-green-900"
                      >
                        View Transaction
                      </a>
                      <p className="text-sm mt-1">
                        Your loan has been repaid successfully.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-6 text-gray-500">
                <p>You need collateral tokens to apply for or repay loans.</p>
              </div>
            )}
          </div>

          {/* User Verification Check Section */}
          <div className="bg-white shadow-md rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">
              Check User Verification
            </h2>
            <p className="text-gray-600 mb-4">
              Check if a user is verified for humanity with the default required claims (24 hour max age).
            </p>

            <div className="space-y-4">
              {/* User Address Input */}
              <div>
                <label
                  htmlFor="verificationAddress"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  User Address to Check
                </label>
                <input
                  id="verificationAddress"
                  type="text"
                  value={verificationAddress}
                  onChange={(e) => setVerificationAddress(e.target.value)}
                  placeholder="0x..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setVerificationAddress(address || "")}
                  disabled={!address}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400"
                >
                  Use my address
                </button>
              </div>

              {/* Default Settings Display */}
              <div className="bg-gray-50 p-4 rounded-md space-y-2">
                {/*<div>*/}
                {/*	<span className="text-sm font-medium text-gray-700">Max Age:</span>{" "}*/}
                {/*	<span className="text-sm text-gray-600">24 hours</span>*/}
                {/*</div>*/}
                <div>
                  <span className="text-sm font-medium text-gray-700">Required Claims:</span>{" "}
                  <div className="mt-1">
                    {constants?.REQUIRED_CLAIMS && constants.REQUIRED_CLAIMS.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {constants.REQUIRED_CLAIMS.map((claim, index) => (
                          <span
                            key={claim + index.toString()}
                            className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-100 text-blue-800"
                          >
                            {claim}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500">Loading...</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Check Button */}
              <button
                type="button"
                onClick={handleCheckUserVerification}
                disabled={
                  isCheckingVerification ||
                  !verificationAddress.trim()
                }
                className="w-full bg-purple-500 hover:bg-purple-600 disabled:bg-purple-300 text-white font-medium py-3 px-6 rounded-lg transition-colors"
              >
                {isCheckingVerification ? "Checking..." : "Check Verification"}
              </button>

              {/* Verification Result */}
              {verificationResult && (
                <div
                  className={`p-4 rounded-lg border ${verificationResult.verified
                    ? "bg-green-50 border-green-200 text-green-800"
                    : "bg-red-50 border-red-200 text-red-800"
                    }`}
                >
                  <div className="space-y-2">
                    <p>
                      <strong>Verification Status:</strong>{" "}
                      {verificationResult.verified
                        ? "✅ Verified"
                        : "❌ Not Verified"}
                    </p>
                    {verificationResult.verified && (
                      <>
                        <div>
                          <strong>Attestation UID:</strong>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="font-mono text-xs bg-white bg-opacity-50 px-2 py-1 rounded">
                              {verificationResult.attestationUID.slice(0, 10)}...{verificationResult.attestationUID.slice(-8)}
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                navigator.clipboard.writeText(verificationResult.attestationUID);
                              }}
                              className="bg-white bg-opacity-30 hover:bg-opacity-50 text-green-700 text-xs px-2 py-1 rounded transition-colors"
                              title="Copy full Attestation UID"
                            >
                              📋
                            </button>
                          </div>
                        </div>
                        <p>
                          <strong>Expires At:</strong>{" "}
                          {(verificationResult.expiresAt).toString()}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Revoke Verification Section */}
              <div className="border-t pt-6 mt-6">
                <h3 className="text-lg font-medium mb-3 text-red-700">
                  Revoke Verification Result
                </h3>
                <p className="text-gray-600 mb-4 text-sm">
                  Revoke an existing verification attestation using its UID.
                </p>

                <div className="space-y-3">
                  <div>
                    <label
                      htmlFor="attestationUID"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Attestation UID
                    </label>
                    <div className="relative">
                      <input
                        id="attestationUID"
                        type="text"
                        value={attestationUID}
                        onChange={(e) => {
                          setAttestationUID(e.target.value);
                          // Clear previous messages when user starts typing
                          setRevokeVerificationError(null);
                          setRevokeVerificationSuccess(null);
                        }}
                        placeholder="0x... (Enter attestation UID manually or use prefill button)"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                      />
                      {verificationResult?.verified && (
                        <button
                          type="button"
                          onClick={() =>
                            setAttestationUID(verificationResult.attestationUID)
                          }
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-blue-500 hover:bg-blue-600 text-white text-xs px-2 py-1 rounded"
                          title="Use UID from verification result above"
                        >
                          Prefill
                        </button>
                      )}
                    </div>

                    {/* Helper text and prefill option */}
                    <div className="mt-2 space-y-1">
                      <p className="text-xs text-gray-500">
                        You can manually enter an attestation UID or use the
                        verification result above.
                      </p>
                      {verificationResult?.verified ? (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-green-600">
                            ✓ Available from verification result:
                          </span>
                          <code className="text-xs bg-gray-100 px-1 rounded">
                            {verificationResult.attestationUID.slice(0, 10)}...
                          </code>
                          <button
                            type="button"
                            onClick={() =>
                              setAttestationUID(
                                verificationResult.attestationUID,
                              )
                            }
                            className="text-xs text-blue-600 hover:text-blue-800 underline"
                          >
                            Use this UID
                          </button>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400">
                          💡 Tip: Check a user's verification above to get their
                          attestation UID automatically.
                        </p>
                      )}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() =>
                      handleRevokeVerificationRequest(attestationUID)
                    }
                    disabled={
                      revokeVerificationLoading || !attestationUID.trim()
                    }
                    className="w-full bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white font-medium py-3 px-6 rounded-lg transition-colors"
                  >
                    {revokeVerificationLoading
                      ? "Revoking..."
                      : "Revoke Verification Result"}
                  </button>

                  {/* Revoke Verification Error Display */}
                  {revokeVerificationError && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mt-4">
                      <strong>Error:</strong> {revokeVerificationError}
                    </div>
                  )}

                  {/* Revoke Verification Success Display */}
                  {revokeVerificationSuccess && (
                    <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mt-4">
                      <strong>Verification revoked successfully!</strong>{" "}
                      <a
                        href={revokeVerificationSuccess}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-800 underline hover:text-green-900"
                      >
                        View Transaction
                      </a>
                      <p className="text-sm mt-1">
                        Please recheck verification status using "Check User Verification"</p>
                    </div>
                  )}

                </div>
              </div>
            </div>
          </div>

          {/* Update Required Claims Section */}
          <div className="bg-white shadow-md rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">
              Update Required Claims
            </h2>
            <p className="text-gray-600 mb-4">
              Manage the list of claims required for humanity verification. Only contract admins can update this.
            </p>

            <div className="space-y-4">
              {/* Current Required Claims Display */}
              {constants?.REQUIRED_CLAIMS && constants.REQUIRED_CLAIMS.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-blue-900 mb-2">
                    Current Required Claims:
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {constants.REQUIRED_CLAIMS.map((claim) => (
                      <span
                        key={claim}
                        className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-mono"
                      >
                        {claim}
                      </span>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={handleInitializeFromCurrentClaims}
                    className="mt-3 text-sm text-blue-600 hover:text-blue-800"
                  >
                    Load current claims to edit
                  </button>
                </div>
              )}

              {/* Add New Claim Input */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newClaimInput}
                  onChange={(e) => setNewClaimInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddClaim();
                    }
                  }}
                  placeholder="Enter claim name (e.g., zkKYC)"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={handleAddClaim}
                  disabled={!newClaimInput.trim()}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  Add Claim
                </button>
              </div>

              {/* Claims to Update List */}
              {claimsToUpdate.length > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">
                    New Claims List ({claimsToUpdate.length}):
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {claimsToUpdate.map((claim) => (
                      <div
                        key={claim}
                        className="bg-white border border-gray-300 text-gray-800 px-3 py-1 rounded-full text-sm font-mono flex items-center gap-2"
                      >
                        <span>{claim}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveClaim(claim)}
                          className="text-red-600 hover:text-red-800 font-bold"
                          aria-label="Remove claim"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => setClaimsToUpdate([])}
                    className="mt-3 text-sm text-gray-600 hover:text-gray-800"
                  >
                    Clear all
                  </button>
                </div>
              )}

              {/* Update Button */}
              <button
                type="button"
                onClick={handleUpdateRequiredClaims}
                disabled={updateClaimsLoading || !contractService || claimsToUpdate.length === 0}
                className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {updateClaimsLoading ? "Updating..." : "Update Required Claims"}
              </button>

              {/* Error Display */}
              {updateClaimsError && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                  <strong>Error:</strong> {updateClaimsError}
                </div>
              )}

              {/* Success Display */}
              {updateClaimsSuccess && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                  <strong>Success!</strong> Required claims updated successfully.{" "}
                  <a
                    href={updateClaimsSuccess}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-800 underline hover:text-green-900"
                  >
                    View Transaction
                  </a>
                  <p className="text-sm mt-1">
                    The updated claims are now shown in "Current Required Claims" above.
                  </p>
                </div>
              )}

              {/* Info Notice */}
              <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded text-sm">
                <strong>Note:</strong> Only users with DEFAULT_ADMIN_ROLE can update required claims.
                This action will replace the entire list of required claims with your new list.
              </div>
            </div>
          </div>

        </>
      )}
    </div>
  );
};

export default WagmiLendingProtocolDApp;
